import { isAxiosError } from 'axios';

import {
  billingRefusalFromProblem,
  type BillingRefusal,
} from '@/lib/billing-refusal';
import { i18n } from '@/lib/i18n/i18n';
import type { ErrorResponse } from '@/types/generated';

// The request never reached the server, so no fallback a call site could write is right:
// nothing about the action failed, the connection did.
export const networkErrorDetail = (): string =>
  i18n.t('request.error_network', { ns: 'common' });

export interface ServerFieldError<Name extends string> {
  name: Name;
  message: string;
}

// Both 422s and domain errors key `TypedApiError.fields` on the bare field name, so a form claims
// what it renders.
export function serverFieldErrors<Name extends string>(
  fields: Record<string, string[]>,
  claimed: readonly Name[],
): ServerFieldError<Name>[] {
  return claimed
    .map((name) => ({ name, message: fields[name]?.[0] }))
    .filter((entry): entry is ServerFieldError<Name> => Boolean(entry.message));
}

// Thrown by the typed-client repository methods on a non-2xx response, carrying the RFC 7807
// body directly — a caller reads `.detail`/`.fields`/`.type` directly off it.
export class TypedApiError extends Error {
  readonly type: string;
  readonly status: number;
  readonly detail: string;
  readonly fields: Record<string, string[]>;
  readonly sessionExpired: boolean;
  readonly code: string | null;
  readonly context: Record<string, unknown> | null;
  readonly billing: BillingRefusal | null;

  constructor(body: ErrorResponse) {
    super(body.title);
    this.name = 'TypedApiError';
    this.type = body.type;
    this.status = body.status;
    this.detail = body.detail;
    this.fields = body.fields ?? {};
    this.sessionExpired = Boolean(body.session_expired);
    this.code = body.code ?? null;
    this.context = body.context ?? null;
    this.billing = billingRefusalFromProblem(body.status, body);
  }
}

// A non-2xx that carried no problem body — a proxy's own error page, a gateway timeout, a
// dropped upstream. Deliberately NOT a `TypedApiError`: call sites read that as "the server
// said why", and this response did not, so they fall back to their own copy instead of
// showing a bare status line.
export class OpaqueApiError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Request failed (${status})`);
    this.name = 'OpaqueApiError';
    this.status = status;
  }
}

// The status wins over the body's copy: a proxy's own JSON may omit it, and callers branch on it.
export const apiErrorFrom = (
  status: number,
  body: unknown,
): TypedApiError | OpaqueApiError => {
  const problem = body as Partial<ErrorResponse> | null;
  if (typeof problem?.detail !== 'string') return new OpaqueApiError(status);
  return new TypedApiError({ ...(problem as ErrorResponse), status });
};

// The one predicate every migrated call site needs. Three cases, in order: the server named the
// reason, so show it; the request never reached the server, so say that rather than blaming the
// action — a "check your CSV" toast sent to someone whose Wi-Fi dropped costs them a re-export;
// otherwise the response carried no problem body and the caller's own copy is the best there is.
export const typedErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof TypedApiError) return error.detail;
  if (isAxiosError(error) && !error.response) return networkErrorDetail();
  return fallback;
};

// A 403 at a sign-in door is a proven account the backend refuses, and its detail says why.
export const signInRefusal = (error: unknown): string | null =>
  error instanceof TypedApiError && error.status === 403 ? error.detail : null;
